// Synced with: functions/modules/validation/schemas/auth.schema.js

import { z } from "zod";
import type { TFunction } from "i18next";
import { SPECIALTY_COUNTRIES } from "../../../constants/specialtyCountries";

// ─── Reusable primitives ──────────────────────────────────────────────────────
// Mirror of backend primitives — same rules, no server transforms on frontend

const emailField = z.email("Please enter a valid email address.").max(254);

// Backend: .trim().length(6) — exact 6 chars after trim (not regex, no digit-only rule)
// Digit-only enforcement is handled at the input level via Controller onChange
const otpField = z.string().trim().length(6, "Please enter the 6-digit code.");

const passwordField = z
  .string({ error: "Password is required." })
  .min(8, "Password must be at least 8 characters.")
  .max(128, "Password is too long.")
  .refine((p) => /[A-Z]/.test(p), "Must contain at least one uppercase letter.")
  .refine((p) => /[a-z]/.test(p), "Must contain at least one lowercase letter.")
  .refine((p) => /[0-9]/.test(p), "Must contain at least one number.");

// Backend: .trim().length(6) — same pattern as otpField
const totpField = z
  .string({ error: "Authenticator code is required." })
  .trim()
  .length(6, "Google Authenticator code must be 6 digits.");

// ─── Allowed specialties ─────────────────────────────────────────────────────
// Synced with: functions/modules/config/specialties.json
// Single source of truth — import from here instead of constants/specialties.ts
export const ALLOWED_SPECIALTIES = [
  "Dentist",
  "Psychiatrist",
  "General Practitioner",
  "Specialist Physician",
  "Other",
] as const;

// ─── Step schemas ─────────────────────────────────────────────────────────────

export const emailStepSchema = z.object({
  email: emailField,
});

export const otpStepSchema = z.object({
  code: otpField,
});

export const passwordLoginSchema = z.object({
  // Frontend-only: min(1) sufficient — Firebase handles actual auth validation
  password: z.string({ error: "Password is required." }).min(1, "Password is required."),
});

export const registerSchema = z
  .object({
    password: passwordField,
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: "Passwords do not match.",
    path: ["confirmPassword"],
  });

export const forgotNewPasswordSchema = registerSchema;

export const totpSchema = z.object({
  totpCode: totpField,
});

// ─── Profile setup schema ─────────────────────────────────────────────────────
// Synced with: createUserProfileSchema in auth.schema.js
// Messages are localized via `t` (auth:errors.*) so validation errors match the active language.
// Note: the backend additionally requires termsAccepted: true. It is not a
// form field here — ProfileSetupModal tracks the checkbox in local state,
// gates submit on it, and passes it in the callable payload.

export const createUserProfileSchema = (t: TFunction<["auth", "common"]>) =>
  z.object({
    // Frontend requires names even though backend marks them optional — preserves existing UX
    firstName: z.string().min(1, t("auth:errors.firstNameRequired")).max(100),
    lastName: z.string().min(1, t("auth:errors.lastNameRequired")).max(100),
    practiceName: z.string().trim().min(1, t("auth:errors.practiceNameRequired")).max(200),
    country: z.enum(SPECIALTY_COUNTRIES, { error: t("auth:errors.practiceCountryRequired") }),
    positionInPractice: z.string().min(1, t("auth:errors.fieldOfPracticeRequired")),
    specialty: z.string().min(1).optional(),
    phoneNumber: z
      .string({ error: t("auth:errors.phoneNumberRequired") })
      .min(1, t("auth:errors.phoneNumberRequired"))
      .max(20, t("auth:errors.phoneNumberTooLong"))
      .regex(/^[\d\s\-\(\)]+$/, t("auth:errors.phoneNumberInvalidChars")),
  });

// ─── Profile update schema ────────────────────────────────────────────────────
// Direct Firestore write — client-side enforcement only (no Cloud Function).
// firstName is required; lastName and positionInPractice are optional updates.

export const updateUserProfileSchema = z.object({
  firstName: z
    .string({ error: "First name is required." })
    .min(1, "Please enter your first name.")
    .max(100, "First name must be at most 100 characters.")
    .transform((v) => v.trim()),
  lastName: z
    .string()
    .max(100, "Last name must be at most 100 characters.")
    .transform((v) => v.trim())
    .optional(),
  positionInPractice: z
    .string()
    .min(1, "Please select a field of practice.")
    .optional(),
  specialty: z.string().min(1).optional(),
});

// ─── Inferred types ───────────────────────────────────────────────────────────

export type EmailStepInput = z.infer<typeof emailStepSchema>;
export type OtpStepInput = z.infer<typeof otpStepSchema>;
export type PasswordLoginInput = z.infer<typeof passwordLoginSchema>;
export type RegisterInput = z.infer<typeof registerSchema>;
export type TotpInput = z.infer<typeof totpSchema>;
export type CreateUserProfileInput = z.infer<ReturnType<typeof createUserProfileSchema>>;
export type UpdateUserProfileInput = z.infer<typeof updateUserProfileSchema>;
export type PositionInPractice = string;
// Semantic aliases — same shape as RegisterInput but named for their context
export type ForgotNewPasswordInput = RegisterInput;
export type GoogleLinkPasswordInput = RegisterInput;
